import _axios from "@/axios";
import { ref, reactive } from "@vue/composition-api";
import { showSnackBar } from "@/composables/snackbar";
import { handleFormErrors } from "@/composables/form-error";

const nas = ref({});
const criteriaNas = ref({
  nasIdentifier: "",
  ipPub: "",
  lastAuthAt: "",
});
const currentNas = ref(null);

export function useNas() {
  const nasTypes = ref([]);
  const nasData = reactive({
    options: {},
    firstLoad: true,
    isLoading: false,
    createNasDialog: false,
    editNasDialog: false,
    nasForm: {
      monitoringType: null,
      nasType: null,
      active: true,
    },
    nasFormValid: false,
    rules: {
      descriptionRules: [
        (v) => !!v || "La description est obligatoire",
        (v) =>
          (v && v.length >= 2 && v.length <= 32) || "entre 2 et 45 caractères",
      ],
      nas_identifierRules: [
        (v) => !!v || "Le NAS-ID est obligatoire",
        (v) => (v && v.length <= 32) || "32 caractères MAX",
        (v) => !/[^a-zA-Z0-9_-]/.test(v) || "Pas de caractères spéciaux",
      ],
      passwordRules: [
        (v) => !/[^a-zA-Z0-9_-]/.test(v) || "Pas de caractères spéciaux",
        (v) => (v && v.length >= 8) || "8 caractères MIN",
      ],
      nasTypeRules: [(v) => !!v || "Le type est obligatoire"],
    },
    nasFormRef: null,
    nasType: [],
    attributes: [],
  });

  const listParams = {
    partnerId: null,
    clientId: null,
  };

  function listNasType() {
    return _axios
      .get("/nas-type")
      .then((response) => {
        nasTypes.value = response.data.data;
      })
      .catch(() => {
        alert(
          "Une erreur est survenue, si cela se reproduit contactez le support"
        );
      });
  }

  function listUpdateNas(page = 1) {
    listNas(page, listParams.partnerId, listParams.clientId);
  }

  async function searchNas(searchTerm) {
    return listNas(1, null, null, searchTerm, null);
  }

  function listNas(
    partnerId = null,
    clientId = null,
    search = null,
    filterParams = null
  ) {
    nasData.isLoading = true;

    const params = new URLSearchParams();

    params.set("page", nasData.options.page ?? 1);
    params.set("itemsPerPage", nasData.options.itemsPerPage ?? 100);

    if (nasData.options.sortBy && nasData.options.sortBy.length > 0) {
      if (nasData.options.sortDesc[0]) {
        params.set("order[desc]", nasData.options.sortBy[0]);
      } else {
        params.set("order[asc]", nasData.options.sortBy[0]);
      }
    }
    if (search != null) {
      params.set("searchTerm", search);
    }

    if (filterParams !== null) {
      if (
        filterParams.nasIdentifier !== "" &&
        filterParams.nasIdentifier !== undefined
      ) {
        params.set("nasIdentifier[partial]", filterParams.nasIdentifier);
      }
      if (filterParams.ipPub !== "" && filterParams.ipPub !== undefined) {
        params.set("publicIp[partial]", filterParams.ipPub);
      }
      if (
        filterParams.lastAuthAt !== "" &&
        filterParams.lastAuthAt !== undefined
      ) {
        params.set("lastAuthAt[after]", filterParams.lastAuthAt);
      }
    }

    let routePath = "/nas";

    if (clientId != null) {
      listParams.clientId = clientId;
      routePath = "/list-nas/portal/" + clientId + "?" + params.toString();
    } else if (partnerId != null) {
      listParams.partnerId = partnerId;
      routePath = "/list-nas/partner/" + partnerId + "?" + params.toString();
    } else {
      routePath = "/list-nas" + "?" + params.toString();
    }

    return _axios
      .get(routePath)
      .then((response) => {
        nas.value = response.data;
      })
      .catch(() => {
        const errorMessage =
          "Une erreur est survenue, si cela se reproduit contactez le support";
        alert(errorMessage);
      })
      .finally(() => {
        nasData.isLoading = false;
        nasData.firstLoad = false;
      });
  }

  function showNas(id) {
    return _axios
      .get("/nas/" + id)
      .then((response) => {
        const nas = response.data.data;
        nas.isActive = nas.active == 1 || nas.active == 2;
        nas.silence = nas.active == 2;
        currentNas.value = nas;
      })
      .catch(() => {
        alert(
          "Une erreur est survenue, si cela se reproduit contactez le support"
        );
      });
  }

  function showNasAttribute(id) {
    nasData.isLoading = true;
    return _axios
      .get("/nas-attribute/" + id)
      .then((response) => {
        nasData.attributes = response.data.data;
        nasData.isLoading = false;
      })
      .catch(() => {
        alert(
          "Une erreur est survenue, si cela se reproduit contactez le support"
        );
      });
  }

  function createNas(clientId) {
    nasData.createNasDialog = true;
    nasData.isLoading = true;
    if (nasData.nasFormRef != null) {
      const isValid = nasData.nasFormRef.validate();
      if (!isValid) {
        nasData.isLoading = false;
        return;
      }
    }

    nasData.nasForm.client = clientId;
    if (nasData.nasForm.active == true) {
      nasData.nasForm.active = 1;
    } else {
      nasData.nasForm.active = 0;
    }
    nasData.isLoading = true;
    return _axios
      .post("/create-nas", nasData.nasForm)
      .then(() => {
        nasData.createNasDialog = false;
        nasData.isLoading = false;
        listNas(null, clientId);
        showSnackBar();
      })
      .catch((error) => {
        const formErrors = error.response.data.violations;
        handleFormErrors(formErrors, nasData.rules, nasData.nasFormRef);
      })
      .finally(() => {
        nasData.isLoading = false;
      });
  }

  function updateNas(nas) {
    nasData.isLoading = true;
    nasData.editNasDialog = true;
    if (nasData.nasFormRef != null) {
      const isValid = nasData.nasFormRef.validate();
      if (!isValid) {
        nasData.isLoading = false;
        return;
      }
    }
    if (nasData.nasForm.active == true) {
      nasData.nasForm.active = 1;
    } else {
      nasData.nasForm.active = 0;
    }
    return _axios
      .put("/nas/" + nas.id, nasData.nasForm)
      .then((response) => {
        const nasResponse = response.data.data;
        nasResponse.isActive =
          nasResponse.active == 1 || nasResponse.active == 2;
        nasResponse.silence = nasResponse.active == 2;
        currentNas.value = nasResponse;
        nasData.editNasDialog = false;
        listNas(null, nas.portal.id);
        showSnackBar();
      })
      .catch((error) => {
        const formErrors = error.response.data.violations;
        handleFormErrors(formErrors, nasData.rules, nasData.nasFormRef);
      })
      .finally(() => {
        nasData.isLoading = false;
      });
  }

  function deleteNas(nas) {
    return _axios
      .delete("/nas/" + nas.id)
      .then(() => {
        listNas(null, nas.portal.id);
        showSnackBar();
      })
      .catch(() => {
        alert(
          "Une erreur est survenue, si cela se reproduit contactez le support"
        );
      });
  }

  return {
    nas,
    criteriaNas,
    currentNas,
    listNas,
    listNasType,
    showNas,
    createNas,
    updateNas,
    deleteNas,
    nasData,
    nasTypes,
    showNasAttribute,
    listUpdateNas,
    searchNas,
  };
}
